import React, { useCallback, useEffect, useMemo } from 'react';

import { Controller, useForm } from 'react-hook-form';
import { getPromoCampaignBenefitOptions } from '~/lib/benefits/promoCampaignBenefits';
import dayjs from '~/timeInit';

import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select as MuiSelect,
  OutlinedInput,
  Stack,
  Typography,
} from '@mui/material';

import { Input } from '~/components/Common/Forms/fields';

import { PROMO_CAMPAIGN_TIERS } from '~/consts/luxLoyalty';

import { useUpdatePromoCampaign } from '~/hooks/apis/promoCampaigns';

import {
  type PromoCampaignPresenter,
  type PromoCampaignTier,
  type PromoCampaignUpdateBody,
  luxLoyaltyRegions,
} from '~/services/LuxLoyalty';

interface UpdatePromoCampaignDialogProps {
  open: boolean;
  campaign: PromoCampaignPresenter | null;
  onClose: (updatedCampaign?: PromoCampaignPresenter) => void;
}

interface UpdatePromoCampaignFormData {
  tiers: Array<PromoCampaignTier>;
  quantity: string;
  startDate: string;
  endDate: string;
}

const formatTierLabel = (tier: PromoCampaignTier) => tier.charAt(0).toUpperCase() + tier.slice(1);

export default function UpdatePromoCampaignDialog({ open, campaign, onClose }: UpdatePromoCampaignDialogProps) {
  const updateMutation = useUpdatePromoCampaign();
  const benefitOptions = useMemo(() => getPromoCampaignBenefitOptions(), []);

  // Track original tiers so we can prevent their removal
  const originalTiers = useMemo<Array<PromoCampaignTier>>(() => {
    if (!campaign || !open) {
      return [];
    }
    return campaign.tiers || [];
  }, [campaign, open]);

  const hasStarted = useMemo(() => {
    if (!campaign) {
      return false;
    }
    return dayjs(campaign.startDate).isBefore(dayjs());
  }, [campaign]);

  const { control, handleSubmit, reset, getValues } = useForm<UpdatePromoCampaignFormData>({
    defaultValues: {
      tiers: [],
      quantity: '',
      startDate: '',
      endDate: '',
    },
    mode: 'onBlur',
  });

  // Populate form when dialog opens with campaign data
  useEffect(() => {
    if (campaign && open) {
      reset({
        tiers: campaign.tiers || [],
        quantity: String(campaign.quantity),
        startDate: dayjs(campaign.startDate).format('YYYY-MM-DD'),
        endDate: dayjs(campaign.endDate).format('YYYY-MM-DD'),
      });
    }
  }, [campaign, open, reset]);

  const buildPayload = useCallback(
    (data: UpdatePromoCampaignFormData): PromoCampaignUpdateBody => {
      const payload: PromoCampaignUpdateBody = {
        tiers: data.tiers,
        quantity: Number(data.quantity),
        endDate: dayjs.tz(data.endDate, 'Australia/Sydney').endOf('day').toISOString(),
      };

      // Only include startDate if campaign hasn't started yet
      if (!hasStarted) {
        payload.startDate = dayjs.tz(data.startDate, 'Australia/Sydney').startOf('day').toISOString();
      }

      return payload;
    },
    [hasStarted],
  );

  const onSubmit = useCallback(
    async (data: UpdatePromoCampaignFormData) => {
      if (!campaign) {
        return;
      }

      try {
        const response = await updateMutation.mutateAsync({
          promoCampaignId: campaign.id,
          campaign: buildPayload(data),
        });
        onClose(response.result.updatedCampaign);
      } catch {
        // Error handled in mutation hook
      }
    },
    [campaign, updateMutation, buildPayload, onClose],
  );

  // Custom onChange for tiers: ensure original tiers can't be removed
  const handleTierChange = useCallback(
    (newTiers: Array<PromoCampaignTier>): Array<PromoCampaignTier> => {
      const result = [...originalTiers];
      newTiers.forEach((tier) => {
        if (!result.includes(tier)) {
          result.push(tier);
        }
      });
      return result;
    },
    [originalTiers],
  );

  return (
    <Dialog open={open} onClose={() => onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Update Promo Campaign</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {campaign && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Name
              </Typography>
              <Typography>{campaign.name}</Typography>
            </Box>
          )}
          {campaign && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Region
              </Typography>
              <Typography>{luxLoyaltyRegions[campaign.region]}</Typography>
            </Box>
          )}
          {campaign && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Benefit
              </Typography>
              <Typography>
                {benefitOptions.find((o) => o.code === campaign.benefit)?.label || campaign.benefit}
              </Typography>
            </Box>
          )}

          <Controller
            name="tiers"
            control={control}
            rules={{
              validate: (value) => value.length > 0 || 'At least one tier is required',
            }}
            render={({ field, fieldState: { error } }) => (
              <FormControl error={!!error} fullWidth required>
                <InputLabel id="promo-campaign-tiers-update-label">Tiers</InputLabel>
                <MuiSelect
                  labelId="promo-campaign-tiers-update-label"
                  multiple
                  value={field.value}
                  onChange={(event) => {
                    const sanitized = handleTierChange(event.target.value as Array<PromoCampaignTier>);
                    field.onChange(sanitized);
                  }}
                  onBlur={field.onBlur}
                  input={<OutlinedInput label="Tiers" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip
                          key={value}
                          label={formatTierLabel(value)}
                          size="small"
                          color={originalTiers.includes(value) ? 'default' : 'primary'}
                        />
                      ))}
                    </Box>
                  )}
                >
                  {PROMO_CAMPAIGN_TIERS.map((tier) => {
                    const isOriginal = originalTiers.includes(tier);
                    return (
                      <MenuItem key={tier} value={tier} disabled={isOriginal && field.value.includes(tier)}>
                        <Checkbox checked={field.value.includes(tier)} disabled={isOriginal} />
                        <ListItemText
                          primary={formatTierLabel(tier)}
                          secondary={isOriginal ? '(cannot remove)' : undefined}
                        />
                      </MenuItem>
                    );
                  })}
                </MuiSelect>
                <FormHelperText>
                  {error?.message || 'Original tiers cannot be removed, only new tiers can be added.'}
                </FormHelperText>
              </FormControl>
            )}
          />

          <Input
            control={control}
            name="quantity"
            muiLabel="Quantity"
            type="number"
            required
            fullWidth
            inputProps={{ min: campaign?.quantity ?? 1 }}
            helperText={campaign ? `Current quantity: ${campaign.quantity}` : ''}
            rules={{
              required: 'Quantity is required',
              validate: (value: string) => {
                if (Number(value) <= 0) {
                  return 'Quantity must be greater than 0';
                }
                if (campaign && Number(value) < campaign.quantity) {
                  return 'Quantity can only be increased';
                }
                return true;
              },
            }}
          />

          <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2}>
            <Input
              control={control}
              name="startDate"
              muiLabel="Start Date"
              type="date"
              required={!hasStarted}
              fullWidth
              disabled={hasStarted}
              helperText={hasStarted ? 'Cannot change after campaign has started' : undefined}
              rules={hasStarted ? {} : { required: 'Start date is required' }}
            />
            <Input
              control={control}
              name="endDate"
              muiLabel="End Date"
              type="date"
              required
              fullWidth
              rules={{
                required: 'End date is required',
                validate: (value: string) => {
                  const startDate = hasStarted ? campaign?.startDate : getValues('startDate');
                  if (startDate && value && dayjs(value).isBefore(dayjs(startDate))) {
                    return 'End date must be after start date';
                  }
                  return true;
                },
              }}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={() => onClose()} disabled={updateMutation.isPending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit(onSubmit)} disabled={updateMutation.isPending}>
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
}
